// URL slugs for villages, points of interest and businesses.
//
// Editors type whatever reads naturally — "Koilani Route", "Όμοδος",
// "Café Platanos" — and the public site needs `koilani-route`, `omodos`,
// `cafe-platanos`. Rather than reject the first spelling, both the admin (as
// the editor leaves the field) and the API (as the last word) turn it into the
// second: lower case, accents dropped, Greek transliterated, every run of
// anything else collapsed to one hyphen.

// ELOT 743, the Greek standard for Latin transliteration, simplified: the
// three common digraphs first (ου → ou, αυ → av, ευ → ev, so Σταυρού reads
// stavrou), then one Latin spelling per letter.
const GREEK_DIGRAPHS: Array<[RegExp, string]> = [
  [/ου/gu, 'ou'],
  [/αυ/gu, 'av'],
  [/ευ/gu, 'ev'],
];
const GREEK: Record<string, string> = {
  α: 'a', β: 'v', γ: 'g', δ: 'd', ε: 'e', ζ: 'z', η: 'i', θ: 'th', ι: 'i',
  κ: 'k', λ: 'l', μ: 'm', ν: 'n', ξ: 'x', ο: 'o', π: 'p', ρ: 'r', σ: 's',
  ς: 's', τ: 't', υ: 'y', φ: 'f', χ: 'ch', ψ: 'ps', ω: 'o',
};

// Length caps, shared by the admin forms and the API so the form refuses
// what the API would, and `toSlug` never drafts a slug either would refuse. POI and business slugs were backfilled from full
// English titles (migration 0010) — the longest live one is 106 characters
// and it is on the public site, so the cap sits above it: shortening a slug
// changes a published URL. Village slugs were always typed by hand.
export const SLUG_MAX_LENGTH = 128;
export const VILLAGE_SLUG_MAX_LENGTH = 64;

// Too long is cut at the last whole word that fits — a POI's slug is drafted
// from its full English title, and some titles are a sentence long.
export function toSlug(input: string, max: number = SLUG_MAX_LENGTH): string {
  let out = input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/gu, ''); // accents, including Greek tonos
  for (const [pattern, latin] of GREEK_DIGRAPHS) out = out.replace(pattern, latin);
  out = out
    .replace(/[α-ως]/gu, (ch) => GREEK[ch] ?? '')
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/^-+|-+$/gu, '');
  if (out.length <= max) return out;
  const cut = out.slice(0, max);
  // Mid-word: step back to the previous hyphen, unless the first word alone
  // is longer than the cap, where a clipped word beats an empty slug.
  const wordEnd = out[max] === '-' ? max : cut.lastIndexOf('-');
  return (wordEnd > 0 ? cut.slice(0, wordEnd) : cut).replace(/-+$/u, '');
}

/** What the API accepts after `toSlug` has run. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/u;
